import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { defaultAskPunttConnectors } from "@mg/schemas/src/commons/enterprise";

import {
  EXACT_CONNECTOR_TOOL_NAMES,
  PROVIDER_SHARED_TOOL_NAMES,
} from "./knownConnectorTools";

import { CONNECTOR_ICONS } from "../constants";

export type UsedConnectorTool = {
  displayName: string;
  count: number;
};

export type UsedConnector = {
  connectorId: string;
  name: string;
  icon: string;
  tools: UsedConnectorTool[];
  callCount: number;
};

type CollectUsedConnectorsOptions = {
  /**
   * When set, only scan messages tied to this review ticket
   * (`viewTicketId` or `ticketIds[]`).
   */
  ticketId?: string;
};

const CONNECTOR_NAME_BY_ID = new Map(
  defaultAskPunttConnectors.map((c) => [c.connectorId, c.name]),
);

const PROVIDER_SHARED_TOOLS = new Set<string>(PROVIDER_SHARED_TOOL_NAMES);

function connectorName(connectorId: string): string {
  return CONNECTOR_NAME_BY_ID.get(connectorId) ?? connectorId;
}

function connectorIcon(connectorId: string): string {
  return CONNECTOR_ICONS[connectorId] ?? "🔗";
}

function displayNameMentions(displayName: string, brand: string): boolean {
  return displayName.toLowerCase().includes(brand.toLowerCase());
}

function providerFromArgs(
  args: Record<string, unknown> | undefined,
): string | null {
  const raw = args?.provider;
  if (typeof raw !== "string") return null;
  const provider = raw.trim().toLowerCase();
  if (provider === "asana" || provider === "wrike") return provider;
  return null;
}

/**
 * Map a tool slug (+ optional display name / args) to a UI connectorId.
 * Returns null for built-ins and unrecognized tools.
 */
export function matchToolToConnectorId(
  tool: string,
  displayName = "",
  args?: Record<string, unknown>,
): string | null {
  const name = tool.trim().toLowerCase();
  if (!name) return null;

  if (PROVIDER_SHARED_TOOLS.has(name)) {
    const fromArgs = providerFromArgs(args);
    if (fromArgs) return fromArgs;
    if (displayNameMentions(displayName, "Asana")) return "asana";
    if (displayNameMentions(displayName, "Wrike")) return "wrike";
    return null;
  }

  const exact = EXACT_CONNECTOR_TOOL_NAMES[name];
  if (exact) return exact;

  if (name.startsWith("asana_")) return "asana";

  if (name.startsWith("wrike_") || name.startsWith("get_wrike_")) {
    return "wrike";
  }

  if (
    name.startsWith("figma_") ||
    name.startsWith("get_figma_") ||
    name.startsWith("list_figma_")
  ) {
    return "figma";
  }

  if (name.startsWith("canva_")) return "canva";

  if (name.startsWith("googledrive_") || name.startsWith("google_drive_")) {
    return "google_drive";
  }

  if (name.startsWith("share_point_") || name.startsWith("sharepoint_")) {
    return "sharepoint";
  }

  if (name.startsWith("jira_")) return "jira";

  if (
    name.startsWith("microsoft_teams_") ||
    name.startsWith("microsoft-teams_") ||
    name.startsWith("teams_")
  ) {
    return "microsoft-teams";
  }

  return null;
}

function messageMatchesTicket(
  message: AskPunttMessage,
  ticketId: string,
): boolean {
  if (message.viewTicketId === ticketId) return true;
  // ticketIds is on the persisted chat message shape; keep a narrow cast until
  // the FE AskPunttMessage export is guaranteed to include it.
  const ticketIds = (message as AskPunttMessage & { ticketIds?: string[] })
    .ticketIds;
  return Array.isArray(ticketIds) && ticketIds.includes(ticketId);
}

type StepHit = {
  connectorId: string;
  displayName: string;
  fromCall: boolean;
};

function extractHits(message: AskPunttMessage): StepHit[] {
  const hits: StepHit[] = [];
  for (const step of message.agentSteps ?? []) {
    if (step.type !== "tool_call" && step.type !== "tool_result") continue;

    const toolName =
      step.type === "tool_result"
        ? step.toolName?.trim() || step.tool
        : step.tool;
    const args =
      step.type === "tool_call" && step.args && typeof step.args === "object"
        ? (step.args as Record<string, unknown>)
        : undefined;
    const connectorId = matchToolToConnectorId(
      toolName,
      step.displayName ?? "",
      args,
    );
    if (!connectorId) continue;

    hits.push({
      connectorId,
      displayName: step.displayName?.trim() || toolName,
      fromCall: step.type === "tool_call",
    });
  }
  return hits;
}

/**
 * Derive which external connectors were actually invoked from chat
 * agentSteps. Dedupes by connectorId and aggregates per-tool display names.
 *
 * Prefers `tool_call` steps for counts so paired call+result does not double.
 * Falls back to `tool_result` only when no calls were recorded for that
 * connector (partial / truncated history).
 *
 * Scope notes:
 * - Without `ticketId`, scans the full message list (e.g. project shared chat
 *   or chat Details for the whole thread). Personal chats inside a project are
 *   not included unless their messages are in that list.
 * - With `ticketId`, only messages whose `viewTicketId` / `ticketIds` match.
 */
export function collectUsedConnectors(
  messages: readonly AskPunttMessage[] | undefined | null,
  options: CollectUsedConnectorsOptions = {},
): UsedConnector[] {
  if (!messages?.length) return [];

  const { ticketId } = options;
  const scoped = ticketId
    ? messages.filter((m) => messageMatchesTicket(m, ticketId))
    : [...messages];

  const allHits = scoped.flatMap(extractHits);
  if (allHits.length === 0) return [];

  const connectorIds = Array.from(new Set(allHits.map((h) => h.connectorId)));
  const result: UsedConnector[] = [];

  for (const connectorId of connectorIds) {
    const hits = allHits.filter((h) => h.connectorId === connectorId);
    const callHits = hits.filter((h) => h.fromCall);
    const counted = callHits.length > 0 ? callHits : hits;

    const toolCounts = new Map<string, number>();
    for (const hit of counted) {
      toolCounts.set(
        hit.displayName,
        (toolCounts.get(hit.displayName) ?? 0) + 1,
      );
    }

    const tools = Array.from(toolCounts.entries())
      .map(([displayName, count]) => ({ displayName, count }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    result.push({
      connectorId,
      name: connectorName(connectorId),
      icon: connectorIcon(connectorId),
      tools,
      callCount: counted.length,
    });
  }

  return result.sort((a, b) => a.name.localeCompare(b.name));
}
